import { getSupabase, supabase } from "../supabase/client";
import type { PropertyTemplate } from "../../types/property";
import { ICurrency } from "@/types";

type PropertyRow = {
  id: string;
  name: string;
  country: string | null;
  city: string | null;
  address: string | null;
  photo_url: string | null;
  type: string | null;
  currency: ICurrency;
  stars: number;
  gfa_sqm: number | null;
  purchase_price: number | null;
  total_rooms: number | null;
  facilities: string[] | null;
  featured: boolean | null;
  show_in_templates: boolean | null;
  show_in_gallery: boolean | null;
  slug: string | null;
  created_at: string | null;
  updated_at: string | null;
  description: string | null;
  investment_thesis: string | null;
  kpis: any | null;
};

export type SlimPropertyTemplate = {
  id: string;
  slug: string;
  dealName: string;
  city: string | null;
  country: string | null;
  fullAddress: string | null;
  photoUrl: string | null;
  propertyType: string;
  stars: number;
  currency: ICurrency;
  gfaSqm: number;
  purchasePrice: number;
  total_rooms: number;
  facilities: string[];
  featured: boolean;
  description?: string;
  investment_thesis?: string;
  showInTemplates: boolean;
  showInGallery: boolean;
};

function mapPropertyTypeToDb(displayType: string): string {
  const typeMap: Record<string, string> = {
    "Boutique Hotel": "hotel",
    Resort: "resort",
    Hostel: "hostel",
    Other: "other",
  };

  return (typeMap[displayType] || "hotel").toLowerCase();
}

function mapDbToPropertyType(dbType: string): string {
  const typeMap: Record<string, string> = {
    hotel: "Boutique Hotel",
    resort: "Resort",
    hostel: "Hostel",
    other: "Other",
  };

  return typeMap[dbType?.toLowerCase()] || "Boutique Hotel";
}

function rowToTemplate(row: PropertyRow): PropertyTemplate {
  return {
    id: row.id,
    slug: row.slug || "",
    dealName: row.name,
    city: row.city || "",
    country: row.country || "",
    fullAddress: row.address || "",
    photoUrl: row.photo_url || "",
    propertyType: mapDbToPropertyType(row.type),
    featured: row.featured || false,
    stars: ((row.kpis?.stars || row.stars) as 1 | 2 | 3 | 4 | 5) || 4,
    currency: row.currency,
    gfaSqm: row.gfa_sqm || 0,
    purchasePrice: row.purchase_price || 0,
    rooms_total: row.total_rooms || 0,
    facilities: Array.isArray(row.facilities) ? (row.facilities as any) : [],
    showInTemplates: row.show_in_templates !== false,
    showInGallery: row.show_in_gallery !== false,
    description: row.description || "",
    investment_thesis: row.investment_thesis || "",
    kpis: row.kpis || {},
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
  };
}

export async function fetchAllProperties(): Promise<PropertyTemplate[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[propertyService] Failed to fetch properties:", error);
    throw new Error(`Failed to fetch properties: ${error.message}`);
  }

  return (data || []).map(rowToTemplate);
}

export async function fetchFeaturedProperties(): Promise<PropertyTemplate[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("featured", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error(
      "[propertyService] Failed to fetch featured properties:",
      error,
    );
    throw new Error(`Failed to fetch featured properties: ${error.message}`);
  }

  return (data || []).map(rowToTemplate);
}

export async function fetchGalleryProperties(): Promise<PropertyTemplate[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("show_in_gallery", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error(
      "[propertyService] Failed to fetch gallery properties:",
      error,
    );
    throw new Error(`Failed to fetch gallery properties: ${error.message}`);
  }

  return (data || []).map(rowToTemplate);
}

export async function fetchTemplateProperties(): Promise<PropertyTemplate[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("show_in_templates", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error(
      "[propertyService] Failed to fetch template properties:",
      error,
    );
    throw new Error(`Failed to fetch template properties: ${error.message}`);
  }

  return (data || []).map(rowToTemplate);
}

export async function fetchPropertyById(
  id: string,
): Promise<PropertyTemplate | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    console.error(`[propertyService] Failed to fetch property ${id}:`, error);
    throw new Error(`Failed to fetch property: ${error.message}`);
  }

  return data ? rowToTemplate(data as PropertyRow) : null;
}

export const createProperty = async (property: PropertyTemplate) => {
  const roomMix = property.kpis?.roomMix || [];

  const dbPropertyType = mapPropertyTypeToDb(property.propertyType);

  const response = await supabase
    .from("properties")
    .insert([
      {
        id: property.id,
        slug: property.slug,
        name: property.name || property.dealName,
        city: property.city,
        country: property.country,
        address: property.fullAddress,
        photo_url: property.photoUrl,
        type: dbPropertyType,
        featured: property.featured,
        stars: property.stars,
        currency: property.currency,
        gfa_sqm: property.gfaSqm,
        purchase_price: property.purchasePrice,
        total_rooms: property.rooms_total,
        facilities: property.facilities,
        show_in_templates: property.showInTemplates,
        show_in_gallery: property.showInGallery,
        description: property.description,
        investment_thesis: property.investment_thesis,
        kpis: {
          ...property.kpis,
          dealName: property.dealName,
          location: `${property.city}, ${property.country}`,
          address: property.fullAddress,
          photoUrl: property.photoUrl,
          propertyType: property.propertyType,
          currency: property.currency,
          gfaSqm: property.gfaSqm,
          purchase: { price: property.purchasePrice },
          purchasePrice: property.purchasePrice,
          rooms: { total: property.rooms_total },
          roomsTotal: property.rooms_total,
          roomMix,
          facilities: property.facilities,
          normalizedFacilities: property.facilities,
          property: { facilities: property.facilities },
        },
        updated_at: property.updatedAt,
      },
    ])
    .select()
    .single();

  if (response.error) throw response.error;
  return mapPropertyFromDB(response.data);
};

export const updateProperty = async (
  id: string,
  property: Partial<PropertyTemplate>,
) => {
  const roomMix = property.kpis?.roomMix || [];

  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (property.dealName !== undefined || (property as any).name !== undefined)
    updateData.name = property.dealName || (property as any).name;
  if (property.city !== undefined) updateData.city = property.city;
  if (property.country !== undefined) updateData.country = property.country;
  if (property.fullAddress !== undefined)
    updateData.address = property.fullAddress;
  if (property.photoUrl !== undefined) updateData.photo_url = property.photoUrl;
  if (property.propertyType !== undefined) {
    updateData.type = mapPropertyTypeToDb(property.propertyType);
  }
  if (property.featured !== undefined) updateData.featured = property.featured;
  if (property.stars !== undefined) updateData.stars = property.stars;
  if (property.currency !== undefined) updateData.currency = property.currency;
  if (property.gfaSqm !== undefined) updateData.gfa_sqm = property.gfaSqm;
  if (property.purchasePrice !== undefined)
    updateData.purchase_price = property.purchasePrice;
  if (property.rooms_total !== undefined)
    updateData.total_rooms = property.rooms_total;
  if (property.facilities !== undefined)
    updateData.facilities = property.facilities;
  if (property.showInTemplates !== undefined)
    updateData.show_in_templates = property.showInTemplates;
  if (property.showInGallery !== undefined)
    updateData.show_in_gallery = property.showInGallery;
  if (property.description !== undefined)
    updateData.description = property.description;
  if (property.investment_thesis !== undefined)
    updateData.investment_thesis = property.investment_thesis;

  if (property.kpis !== undefined) {
    updateData.kpis = {
      ...property.kpis,
      roomMix: property.kpis.roomMix || [],
    };
  }

  const response = await supabase
    .from("properties")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (response.error) throw response.error;
  return mapPropertyFromDB(response.data);
};

export async function deleteProperty(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from("properties").delete().eq("id", id);

  if (error) {
    console.error(`[propertyService] Failed to delete property ${id}:`, error);
    throw new Error(`Failed to delete property: ${error.message}`);
  }
}

export async function toggleFeatured(id: string): Promise<PropertyTemplate> {
  const supabase = getSupabase();

  const property = await fetchPropertyById(id);
  if (!property) {
    throw new Error(`Property ${id} not found`);
  }

  const { data, error } = await supabase
    .from("properties")
    .update({ featured: !property.featured })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error(
      `[propertyService] Failed to toggle featured for ${id}:`,
      error,
    );
    throw new Error(`Failed to toggle featured: ${error.message}`);
  }

  return rowToTemplate(data as PropertyRow);
}

export async function toggleGallery(id: string): Promise<PropertyTemplate> {
  const supabase = getSupabase();

  const property = await fetchPropertyById(id);
  if (!property) {
    throw new Error(`Property ${id} not found`);
  }

  const { data, error } = await supabase
    .from("properties")
    .update({ show_in_gallery: !property.showInGallery })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error(
      `[propertyService] Failed to toggle gallery for ${id}:`,
      error,
    );
    throw new Error(`Failed to toggle gallery: ${error.message}`);
  }

  return rowToTemplate(data as PropertyRow);
}

export async function toggleTemplates(id: string): Promise<PropertyTemplate> {
  const supabase = getSupabase();

  const property = await fetchPropertyById(id);
  if (!property) {
    throw new Error(`Property ${id} not found`);
  }

  const { data, error } = await supabase
    .from("properties")
    .update({ show_in_templates: !property.showInTemplates })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error(
      `[propertyService] Failed to toggle templates for ${id}:`,
      error,
    );
    throw new Error(`Failed to toggle templates: ${error.message}`);
  }

  return rowToTemplate(data as PropertyRow);
}

export const selectors = {
  forTemplates: fetchTemplateProperties,
  forGallery: fetchGalleryProperties,
  featured: fetchFeaturedProperties,
};

const mapPropertyFromDB = (data: any): PropertyTemplate => {
  return {
    id: data.id,
    slug: data.slug,
    dealName: data.name,
    city: data.city,
    country: data.country,
    fullAddress: data.address,
    photoUrl: data.photo_url,
    propertyType: mapDbToPropertyType(data.type),
    featured: data.featured,
    stars: data.stars || 2,
    currency: data.currency || "EUR",
    gfaSqm: data.gfa_sqm || 0,
    purchasePrice: data.purchase_price || 0,
    rooms_total: data.total_rooms || 0,
    facilities: Array.isArray(data.facilities) ? data.facilities : [],
    showInTemplates: data.show_in_templates !== false,
    showInGallery: data.show_in_gallery !== false,
    description: data.description || "",
    investment_thesis: data.investment_thesis || "",
    kpis: data.kpis || {},
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const getPropertyById = async (id: string) => {
  try {
    const response = await supabase
      .from("properties")
      .select("*")
      .eq("id", id)
      .single();

    if (response.error) {
      if (response.error.code === "PGRST116") {
        return null;
      }
      throw response.error;
    }

    return mapPropertyFromDB(response.data);
  } catch (error) {
    console.error("[propertyService] Exception in getPropertyById:", error);
    throw error;
  }
};

export async function fetchSlimTemplateProperties(): Promise<
  SlimPropertyTemplate[]
> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from("properties")
    .select(
      `
      id,
      slug,
      name,
      city,
      country,
      address,
      photo_url,
      type,
      stars,
      currency,
      gfa_sqm,
      purchase_price,
      total_rooms,
      facilities,
      featured,
      show_in_templates,
      show_in_gallery,
      description,
      investment_thesis
    `,
    )
    .eq("show_in_templates", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error(
      "[propertyService] Failed to fetch slim template properties:",
      error,
    );
    throw new Error(`Failed to fetch template properties: ${error.message}`);
  }

  return (data || []).map((row) => {
    const propertyTypeMap: Record<string, string> = {
      hotel: "Boutique Hotel",
      resort: "Resort",
      hostel: "Hostel",
      other: "Other",
    };

    return {
      id: row.id,
      slug: row.slug || "",
      dealName: row.name || "Unnamed Property",
      city: row.city,
      country: row.country,
      fullAddress: row.address,
      photoUrl: row.photo_url,
      propertyType: propertyTypeMap[row.type || "hotel"] || "Boutique Hotel",
      stars: (row.stars as 1 | 2 | 3 | 4 | 5) || 4,
      currency: row.currency || "EUR",
      gfaSqm: row.gfa_sqm || 0,
      purchasePrice: row.purchase_price || 0,
      total_rooms: row.total_rooms || 0,
      facilities: Array.isArray(row.facilities) ? row.facilities : [],
      featured: row.featured || false,
      showInTemplates: row.show_in_templates !== false,
      showInGallery: row.show_in_gallery !== false,
      description: row.description || "",
      investment_thesis: row.investment_thesis || "",
    };
  });
}

export async function fetchFullTemplateById(
  id: string,
): Promise<PropertyTemplate | null> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("id", id)
    .eq("show_in_templates", true)
    .maybeSingle();

  if (error) {
    console.error(
      `[propertyService] Failed to fetch full template ${id}:`,
      error,
    );
    throw new Error(`Failed to fetch template: ${error.message}`);
  }

  return data ? rowToTemplate(data as PropertyRow) : null;
}
